import { z } from "zod/v4";

/** Object shape for a synced entity: must include `id: z.uuid()`. */
export interface ModelSchema extends z.ZodRawShape {
  id: z.ZodUUID;
}

type ZodPartial<T extends z.ZodRawShape> = {
  [K in keyof T]: K extends "id" ? T[K] : T[K] extends z.ZodTypeAny ? z.ZodOptional<T[K]> : never;
};

/** `objectSchema` + discriminant `type` literal (domain entity). */
export function entityWithType<TSchema extends ModelSchema, const TType extends string>(
  objectSchema: z.ZodObject<TSchema>,
  type: TType,
): z.ZodObject<TSchema & { type: z.ZodLiteral<TType> }> {
  return objectSchema.extend({
    type: z.literal(type),
  }) as z.ZodObject<TSchema & { type: z.ZodLiteral<TType> }>;
}

/** Partial update payload: omit readonly keys from the object shape, then partial + required `id` + `type`. */
export function deltaFromObject<
  TSchema extends ModelSchema,
  const TType extends string,
  TReadonly extends Extract<keyof TSchema, string>,
>(
  objectSchema: z.ZodObject<TSchema>,
  type: TType,
  readonlyKeys: readonly TReadonly[],
): z.ZodObject<
  ZodPartial<Omit<TSchema, TReadonly>> & {
    id: z.ZodUUID;
    type: z.ZodLiteral<TType>;
  }
> {
  const deltaMask = Object.fromEntries(
    readonlyKeys.map((key) => [key, true]),
  ) as z.util.Mask<TReadonly> & Record<Exclude<keyof TSchema, keyof TSchema>, never>;

  return objectSchema
    .omit(deltaMask)
    .partial()
    .extend({
      id: objectSchema.shape.id,
      type: z.literal(type),
    }) as z.ZodObject<
    ZodPartial<Omit<TSchema, TReadonly>> & {
      id: z.ZodUUID;
      type: z.ZodLiteral<TType>;
    }
  >;
}
